import { useState } from "react";
import { Plus, Pencil, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, errMsg, testimonialPhotoUrl } from "@/lib/api";
import { F, inputCls, StatusPill, ConfirmDelete, PublishControls, FormButtons, saveItem, MoveButtons, moveItem, ListToolbar, matches } from "@/components/admin/shared";

const blank = { name: "", text: "", category_id: "", rating: "", featured: false, consent_confirmed: false, photo_consent: false };

const PhotoField = ({ f, setF, onSaved }) => {
  const [busy, setBusy] = useState(false);
  if (!f.id) return <p className="text-[11px] text-nanina-muted">Simpan draft dulu untuk menambahkan foto pelanggan.</p>;
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await http.post(`/admin/testimonials/${f.id}/photo`, fd);
      setF({ ...f, photo_path: r.data.photo_path, updated_at: r.data.updated_at });
      toast.success("Foto diunggah");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    const r = await http.delete(`/admin/testimonials/${f.id}/photo`);
    setF({ ...f, photo_path: null, updated_at: r.data.updated_at });
    onSaved();
  };
  return (
    <div className="space-y-2">
      {f.photo_path && (
        <div className="flex items-center gap-3">
          <img src={testimonialPhotoUrl({ ...f, published_at: null })} alt="Foto pelanggan" className="h-14 w-14 rounded-full object-cover" data-testid="testimonial-photo-preview" />
          <button type="button" onClick={remove} className="text-xs text-red-600" data-testid="testimonial-photo-remove">Hapus foto</button>
        </div>
      )}
      <F label="Foto pelanggan (opsional, JPG/PNG/WEBP maks 8 MB)"><input type="file" accept="image/jpeg,image/png,image/webp" className={inputCls} onChange={upload} disabled={busy} data-testid="testimonial-photo-input" /></F>
    </div>
  );
};

const Check = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-nanina-green"><input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} /> {label}</label>
);

const TestimonialForm = ({ initial, categories, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e, publish = false) => {
    e?.preventDefault();
    if (publish && !f.consent_confirmed) return toast.error("Konfirmasi izin pelanggan dulu sebelum menerbitkan.");
    setBusy(true);
    const isNew = !f.id;
    try {
      await saveItem("testimonials", f, { ...f, rating: f.rating ? Number(f.rating) : null }, publish, (d) => setF((x) => ({ ...x, id: d.id })));
      toast.success(publish ? "Testimoni disimpan & diterbitkan" : isNew ? "Draft disimpan. Anda bisa menambahkan foto sekarang." : "Draft testimoni disimpan");
      onSaved();
      if (publish || !isNew) onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid gap-3" data-testid="testimonial-form">
      <F label="Nama tampilan (opsional, sesuai izin)"><input className={inputCls} value={f.name} onChange={set("name")} placeholder="Contoh: Bunda R." data-testid="testimonial-name-input" /></F>
      <F label="Isi testimoni asli *" hint="Jangan memuat data medis atau informasi sensitif."><textarea required maxLength={600} rows={4} className={inputCls} value={f.text} onChange={set("text")} data-testid="testimonial-text-input" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Layanan terkait"><select className={inputCls} value={f.category_id} onChange={set("category_id")} data-testid="testimonial-category-select"><option value="">-</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></F>
        <F label="Rating dari pelanggan"><select className={inputCls} value={f.rating ?? ""} onChange={set("rating")} data-testid="testimonial-rating-select"><option value="">Tanpa rating</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} bintang</option>)}</select></F>
      </div>
      <Check label="Pelanggan telah mengizinkan testimoni dipublikasikan" checked={f.consent_confirmed} onChange={(v) => setF({ ...f, consent_confirmed: v })} testId="testimonial-consent-checkbox" />
      <Check label="Pelanggan mengizinkan fotonya ditampilkan" checked={f.photo_consent} onChange={(v) => setF({ ...f, photo_consent: v })} testId="testimonial-photo-consent-checkbox" />
      <Check label="Jadikan testimoni unggulan" checked={f.featured} onChange={(v) => setF({ ...f, featured: v })} testId="testimonial-featured-checkbox" />
      <PhotoField f={f} setF={setF} onSaved={onSaved} />
      <FormButtons busy={busy} onPublish={() => save(null, true)} testId="testimonial" />
    </form>
  );
};

export const TestimonialsPanel = ({ categories, testimonials, reload }) => {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const remove = async (t) => { try { await http.delete(`/admin/testimonials/${t.id}`); toast.success("Testimoni dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const move = async (i, d) => { if (await moveItem(testimonials, i, d, "/admin/testimonials/reorder")) reload(); };
  const list = testimonials.map((t, i) => ({ t, i })).filter(({ t }) => matches(t, q, status, ["name", "text"]));
  return (
    <div data-testid="testimonials-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-nanina-muted">Hanya masukkan testimoni asli yang telah diizinkan pelanggan. Bagian testimoni tidak tampil bila kosong.</p>
        <button onClick={() => setEditing({ ...blank })} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-nanina-green text-white text-sm font-semibold px-4 py-2" data-testid="testimonial-add-button"><Plus className="h-4 w-4" /> Tambah</button>
      </div>
      <ListToolbar q={q} setQ={setQ} status={status} setStatus={setStatus} testId="testimonial" />
      {testimonials.length === 0 && <p className="rounded-2xl border border-dashed border-nanina-line p-6 text-center text-sm text-nanina-muted">Belum ada testimoni.</p>}
      <ul className="space-y-2">
        {list.map(({ t, i }) => (
          <li key={t.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-nanina-line p-3" data-testid={`admin-testimonial-${t.id}`}>
            <div className="min-w-0 flex-1 basis-full sm:basis-0">
              <p className="text-sm font-semibold text-nanina-green flex items-center gap-1.5">{t.name || "(tanpa nama)"} {t.featured && <Star className="h-3.5 w-3.5 fill-nanina-butter text-nanina-butter" />} {t.rating ? <span className="text-[11px] text-nanina-muted">{t.rating}/5</span> : null}</p>
              <p className="text-xs text-nanina-muted line-clamp-1">{t.text}</p>
              <StatusPill item={t} /> {!t.consent_confirmed && <span className="text-[10px] text-[#8A6D3B]">Izin belum dikonfirmasi</span>}
            </div>
            {t.consent_confirmed && <PublishControls item={t} coll="testimonials" reload={reload} testId={`testimonial-${t.id}`} />}
            {!q && !status && <MoveButtons onUp={() => move(i, -1)} onDown={() => move(i, 1)} testId={`testimonial-move-${t.id}`} />}
            <button onClick={() => setEditing({ ...t })} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Edit" data-testid={`testimonial-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
            <ConfirmDelete label={t.name} onConfirm={() => remove(t)} testId={`testimonial-delete-${t.id}`} />
          </li>
        ))}
      </ul>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit testimoni" : "Tambah testimoni"}</DialogTitle><DialogDescription>Testimoni hanya terbit bila izin dikonfirmasi.</DialogDescription></DialogHeader>
          {editing && <TestimonialForm initial={editing} categories={categories} onSaved={reload} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
